import { DateTime } from "luxon";

import type { AgenticLogicSurface } from "@intelligems/ig-types";

import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@src/store/rootState";
import type { ExperienceModuleTableConfigKey } from "@src/domains/experienceTablePages/experienceModuleConfig";
import {
  ExperienceTableFilters,
  IAppSettingsState,
  LoggedOrg,
  PerformanceView,
} from "../types/appSettings";

export const EMPTY_EXPERIENCE_TABLE_FILTERS: ExperienceTableFilters = {
  categoryTab: "all",
  statusFilters: [],
  modificationFilters: [],
};

export const EMPTY_AGENTIC_LOGIC_RULE_IDEAS: readonly string[] = [];

/**
 * Which set of rule ideas a creation view is showing.
 *
 * Ideas are written for the placement the builder was entered from — an idea
 * offered inside a checkout upsell has to describe a rule checkout can run —
 * so a single per-org cache would hand the checkout entry the onsite set it
 * generated an hour ago. Entries from the rules list, which is bound to no
 * placement, share the `"any"` key.
 */
export const agenticLogicRuleIdeasContextKey = (
  placementSurface?: AgenticLogicSurface
): string => placementSurface ?? "any";

const initialAppSettingsState: IAppSettingsState = {
  isShopLogin: true,
  loggedOrgs: [],
  dismissedAlerts: {},
  dismissedRecommendationCopies: {},
  agenticLogicRuleIdeasByOrgAndContext: {},
  showDuplicateProductIntro: true,
  rememberEmail: undefined,
  onboardingPage: 0,
  showIds: false,
  snoozeShopifyScopesDialog: undefined,
  performanceViews: [{ dataKey: "TBT" }],
  appPadding: true,
  sideNavCollapsed: false,
  experienceTableFilters: {},
  viewAsMode: false,
  viewAsProductId: undefined,
  prefersLegacyEditor: false,
};

const appSettingsSlice = rtk.createSlice({
  name: "appSettings",
  initialState: initialAppSettingsState,
  reducers: {
    setViewAsMode: (appSettings, value: PayloadAction<boolean>) => {
      appSettings.viewAsMode = value.payload;
    },
    setViewAsProductId: (
      appSettings,
      value: PayloadAction<string | undefined>
    ) => {
      appSettings.viewAsProductId = value.payload;
    },
    setShopLogin: (appSettings) => {
      appSettings.isShopLogin = true;
    },
    setEmailLogin: (appSettings) => {
      appSettings.isShopLogin = false;
    },
    toggleShowIds: (appSettings) => {
      appSettings.showIds = !appSettings.showIds;
    },
    disabledDuplicateProductIntro: (appSettings) => {
      appSettings.showDuplicateProductIntro = false;
    },
    rememberEmail: (appSettings, value: PayloadAction<string>) => {
      appSettings.rememberEmail = value.payload;
    },
    clearRememberedEmail: (appSettings) => {
      appSettings.rememberEmail = undefined;
    },
    snoozeShopifyScopesDialog: (appSettings) => {
      appSettings.snoozeShopifyScopesDialog = Math.round(
        DateTime.now().plus({ days: 2 }).toSeconds()
      );
    },
    clearSnoozeShopifyScopesDialog: (appSettings) => {
      appSettings.snoozeShopifyScopesDialog = undefined;
    },
    dismissAlert: (
      appSettings,
      value: PayloadAction<{ orgId: string; alertId: string }>
    ) => {
      if (!(value.payload.orgId in (appSettings.dismissedAlerts || {}))) {
        appSettings.dismissedAlerts[value.payload.orgId] = {};
      }
      if (
        !(
          value.payload.alertId in
          (appSettings.dismissedAlerts[value.payload.orgId] || {})
        )
      ) {
        appSettings.dismissedAlerts[value.payload.orgId][
          value.payload.alertId
        ] = true;
      }
    },
    dismissRecommendationCopy: (
      appSettings,
      value: PayloadAction<{
        orgId: string;
        recommendationKey: string;
        copyHash: string;
      }>
    ) => {
      const { orgId, recommendationKey, copyHash } = value.payload;
      if (!orgId || !recommendationKey || !copyHash) {
        return;
      }

      if (!appSettings.dismissedRecommendationCopies) {
        appSettings.dismissedRecommendationCopies = {};
      }

      if (!(orgId in appSettings.dismissedRecommendationCopies)) {
        appSettings.dismissedRecommendationCopies[orgId] = {};
      }

      appSettings.dismissedRecommendationCopies[orgId][recommendationKey] =
        copyHash;
    },
    setAgenticLogicRuleIdeas: (
      appSettings,
      value: PayloadAction<{
        orgId: string;
        placementSurface?: AgenticLogicSurface;
        ideas: string[];
      }>
    ) => {
      const byOrg = (appSettings.agenticLogicRuleIdeasByOrgAndContext ??= {});
      const byContext = (byOrg[value.payload.orgId] ??= {});
      byContext[
        agenticLogicRuleIdeasContextKey(value.payload.placementSurface)
      ] = value.payload.ideas;
    },
    appendNewOrg: (appSettings, value: PayloadAction<LoggedOrg>) => {
      const exists = appSettings.loggedOrgs.some(
        (o: any) => o.shopId === value.payload.shopId
      );
      if (!exists) {
        appSettings.loggedOrgs.push({
          shopId: value.payload.shopId,
          name: value.payload.name,
        });
      }
    },
    removeOrg: (appSettings, value: PayloadAction<string>) => {
      appSettings.loggedOrgs = appSettings.loggedOrgs.filter(
        (o: LoggedOrg) => o.shopId !== value.payload
      );
    },
    setPerformanceView: (
      appSettings,
      value: PayloadAction<{ index: number; value: PerformanceView }>
    ) => {
      appSettings.performanceViews[value.payload.index] = value.payload.value;
    },
    addPerformanceView: (appSettings) => {
      appSettings.performanceViews.push({ dataKey: "TBT" });
    },
    removePerformanceView: (
      appSettings,
      value: PayloadAction<{ index: number }>
    ) => {
      if (appSettings.performanceViews.length > 1) {
        appSettings.performanceViews.splice(value.payload.index, 1);
      }
    },
    setUseAppPadding: (appSettings, value: PayloadAction<boolean>) => {
      appSettings.appPadding = value.payload;
    },
    setSideNavCollapsed: (appSettings) => {
      appSettings.sideNavCollapsed = !appSettings.sideNavCollapsed;
      return appSettings;
    },
    initSideNavCollapsed: (appSettings, value: PayloadAction<boolean>) => {
      appSettings.sideNavCollapsed = value.payload;
      return appSettings;
    },
    setExperienceTableFilters: (
      appSettings,
      action: PayloadAction<{
        tableKey: ExperienceModuleTableConfigKey;
        filters: Partial<ExperienceTableFilters>;
      }>
    ) => {
      const { tableKey, filters } = action.payload;
      // Rehydrated persisted state predating this field can be missing the map
      // (same situation dismissRecommendationCopy guards against).
      if (!appSettings.experienceTableFilters) {
        appSettings.experienceTableFilters = {};
      }
      appSettings.experienceTableFilters[tableKey] = {
        ...(appSettings.experienceTableFilters[tableKey] ??
          EMPTY_EXPERIENCE_TABLE_FILTERS),
        ...filters,
      };
    },
    clearExperienceTableFilters: (
      appSettings,
      action: PayloadAction<{ tableKey: ExperienceModuleTableConfigKey }>
    ) => {
      if (appSettings.experienceTableFilters) {
        delete appSettings.experienceTableFilters[action.payload.tableKey];
      }
    },
    clearAllExperienceTableFilters: (appSettings) => {
      appSettings.experienceTableFilters = {};
    },
    setPrefersLegacyEditor: (appSettings, action: PayloadAction<boolean>) => {
      appSettings.prefersLegacyEditor = action.payload;
    },
  },
  selectors: {
    selectedViewAsMode: (state) => state.viewAsMode,
    selectedViewAsProductId: (state) => state.viewAsProductId,
    selectedShowDuplicateProductIntro: (state) =>
      state.showDuplicateProductIntro,
    selectedRememberEmail: (state) => state.rememberEmail,
    selectedIsShopLogin: (state) => state.isShopLogin,
    selectedPerformanceViews: (state) => state.performanceViews,
    selectedSideNavCollapsed: (state) => state.sideNavCollapsed,
    selectedPrefersLegacyEditor: (state) => state.prefersLegacyEditor,
  },
});

export const {
  setViewAsMode,
  setViewAsProductId,
  setEmailLogin,
  setShopLogin,
  appendNewOrg,
  removeOrg,
  rememberEmail,
  dismissAlert,
  dismissRecommendationCopy,
  setAgenticLogicRuleIdeas,
  clearRememberedEmail,
  disabledDuplicateProductIntro,
  setPerformanceView,
  addPerformanceView,
  removePerformanceView,
  clearSnoozeShopifyScopesDialog,
  snoozeShopifyScopesDialog,
  setUseAppPadding,
  setSideNavCollapsed,
  initSideNavCollapsed,
  setExperienceTableFilters,
  clearExperienceTableFilters,
  setPrefersLegacyEditor,
  clearAllExperienceTableFilters,
} = appSettingsSlice.actions;

export const localSettingsReducer = appSettingsSlice.reducer;

export const getIsAlertDismissed =
  (orgId?: string, alertId?: string) => (state: RootState) => {
    if (!alertId || !orgId || !state.appSettings.dismissedAlerts) {
      return false;
    }

    if (!(orgId in state.appSettings.dismissedAlerts)) {
      return false;
    }

    return alertId in state.appSettings.dismissedAlerts[orgId];
  };

export const getIsRecommendationCopyDismissed =
  (orgId?: string, recommendationKey?: string, copyHash?: string) =>
  (state: RootState) => {
    if (
      !orgId ||
      !recommendationKey ||
      !copyHash ||
      !state.appSettings.dismissedRecommendationCopies
    ) {
      return false;
    }

    if (!(orgId in state.appSettings.dismissedRecommendationCopies)) {
      return false;
    }

    return (
      state.appSettings.dismissedRecommendationCopies[orgId][
        recommendationKey
      ] === copyHash
    );
  };

export const selectAgenticLogicRuleIdeas =
  (orgId?: string, placementSurface?: AgenticLogicSurface) =>
  (state: RootState) =>
    (orgId &&
      state.appSettings.agenticLogicRuleIdeasByOrgAndContext?.[orgId]?.[
        agenticLogicRuleIdeasContextKey(placementSurface)
      ]) ||
    EMPTY_AGENTIC_LOGIC_RULE_IDEAS;

export const {
  selectedViewAsMode,
  selectedViewAsProductId,
  selectedRememberEmail,
  selectedIsShopLogin,
  selectedPerformanceViews,
  selectedPrefersLegacyEditor,
} = appSettingsSlice.selectors;

export const selectExperienceTableFilters =
  (tableKey: ExperienceModuleTableConfigKey) => (state: RootState) =>
    state.appSettings.experienceTableFilters?.[tableKey] ??
    EMPTY_EXPERIENCE_TABLE_FILTERS;

export const getNeedsWideApp = (state: RootState) =>
  state.variations.variations.length > 6;
